import React, { useState } from "react";
import { PanelContainer } from "@/components/chat/panels/PanelContainer";
import { StickyNote } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { PromptBox } from "@/components/content-chat/PromptBox";
import { GeneratingCard } from "@/components/content-chat/GeneratingCard";
import { SummarySettingsModal, SummarySettings } from "@/components/content-chat/modals";
import { MySummariesList } from "@/components/summaries/MySummariesList";
import SummaryViewer from "@/components/summaries/SummaryViewer";
import { useContentContext } from "@/components/content-chat/ContentContext";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSummaries, updateSummaryTitle, deleteSummary } from "@/features/summaries/api";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { ContentType } from "@/types/content";
import { trackSummaryGenerated } from "@/lib/analytics";

interface ContentSummaryPanelProps {
  isMobile?: boolean;
  contentId?: string | null;
}

export const ContentSummaryPanel: React.FC<ContentSummaryPanelProps> = (props) => {
  const { content } = useContentContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeSummaryId, setActiveSummaryId] = useState<string | null>(null);

  // Determine content type for the settings modal
  const isDocument = content?.type === 'pdf' || content?.type === 'doc' || content?.type === 'text';
  const contentType = isDocument ? 'document' : 'media';

  // Fetch summaries from database
  const { data: summaries = [], isLoading } = useQuery({
    queryKey: ["summaries", user?.id, props.contentId],
    queryFn: () => getSummaries(user!.id, props.contentId),
    enabled: !!user,
  });

  // Rename summary mutation
  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => updateSummaryTitle(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summaries", user?.id, props.contentId] });
      toast({ title: "Summary renamed" });
    },
    onError: () => {
      toast({ title: "Failed to rename summary", variant: "destructive" });
    },
  });

  // Delete summary mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSummary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summaries", user?.id, props.contentId] });
      toast({ title: "Summary deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete summary", variant: "destructive" });
    },
  });

  const handleGenerate = async (selectedOption?: string) => {
    if (!user || !content) return;
    
    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating a summary.",
          variant: "destructive",
        });
        return;
      }

      // Determine template from selected option
      const template = selectedOption || "Medium Summary";

      // Generate summary with AI
      const { data, error } = await supabase.functions.invoke("generate-summary", {
        body: {
          user_id: user.id,
          content_id: content.id,
          template,
          title: `${template} - ${content.title}`,
          source_text: sourceText,
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["summaries", user.id, props.contentId] });
      
      toast({
        title: "Summary generated!",
        description: `Created "${template}" summary`,
      });
      trackSummaryGenerated(data?.id, template);
    } catch (error: any) {
      console.error("Error generating summary:", error);
      
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate summary. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSettings = () => {
    setSettingsOpen(true);
  };

  const handleSettingsSubmit = async (settings: SummarySettings) => {
    if (!user || !content) return;
    
    console.log("Summary settings:", settings);
    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating a summary.",
          variant: "destructive",
        });
        return;
      }

      // Generate summary with custom settings
      const { data, error } = await supabase.functions.invoke("generate-summary", {
        body: {
          user_id: user.id,
          content_id: content.id,
          template: settings.template,
          title: `${settings.template} - ${content.title}`,
          ranges: settings.ranges,
          source_text: sourceText,
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["summaries", user.id, props.contentId] });
      
      toast({
        title: "Summary generated!",
        description: `Created "${settings.template}" summary`,
      });
      trackSummaryGenerated(data?.id, settings.template);
    } catch (error: any) {
      console.error("Error generating summary:", error);
      
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate summary. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleChange = (checked: boolean) => {
    setAutoGenerateEnabled(checked);
    console.log("Auto-generate enabled:", checked);
  };

  const handleRenameSummary = (id: string, newTitle: string) => {
    renameMutation.mutate({ id, title: newTitle });
  };

  const handleDeleteSummary = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleClickSummary = (id: string) => {
    setActiveSummaryId(id);
  };

  const handleBackFromViewer = () => {
    setActiveSummaryId(null);
  };

  // Find the active summary for the viewer
  const activeSummary = activeSummaryId 
    ? summaries.find(s => s.id === activeSummaryId) 
    : null;

  // Check if summaries exist
  const hasSummaries = !isLoading && summaries.length > 0;

  // Map database summaries to component format
  const summaryItems = summaries.map(s => ({
    id: s.id,
    title: s.title,
    type: s.type,
    created_at: s.created_at,
  }));

  if (!user) {
    return (
      <PanelContainer
        {...props}
        title="Summary"
        description={
          <>
            Sign in to save summaries. Ask Shattara AI:{" "}
            <span className="font-bold">"Summarize the key points from this content."</span>
          </>
        }
        icon={<StickyNote className="h-4 w-4 text-primary" />}
      >
        <div className="p-3 text-xs text-muted-foreground">
          Please log in to use summaries.
        </div>
      </PanelContainer>
    );
  }

  // If viewing a summary, show the viewer
  if (activeSummary) {
    return (
      <SummaryViewer
        summary={activeSummary}
        onBack={handleBackFromViewer}
      />
    );
  }

  return (
    <>
      <PanelContainer
        {...props}
        title="Summary"
        description={
          <>
            Try asking Shattara AI:{" "}
            <span className="font-bold">"Summarize the key points from this content."</span>
          </>
        }
        icon={<StickyNote className="h-4 w-4 text-primary" />}
      >
        <div className="flex flex-col">
          {/* Always show PromptBox or GeneratingCard */}
          {isGenerating ? (
            <GeneratingCard type="summary" />
          ) : (
            <PromptBox
              type="summary"
              onGenerate={handleGenerate}
              onSettings={handleSettings}
              onToggleChange={handleToggleChange}
              autoGenerateEnabled={autoGenerateEnabled}
              isGenerating={isGenerating}
            />
          )}
          
          {/* Show loading skeleton while fetching */}
          {isLoading && (
            <div className="p-3 space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border/50">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Show summaries list below when summaries exist */}
          {!isLoading && hasSummaries && (
            <MySummariesList
              summaries={summaryItems}
              onRename={handleRenameSummary}
              onDelete={handleDeleteSummary}
              onClick={handleClickSummary}
            />
          )}
        </div>
      </PanelContainer>
      
      <SummarySettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleSettingsSubmit}
        contentType={contentType}
      />
    </>
  );
};
